/* Altissimo — product page: media gallery, full-screen viewer, variant picker. */
(() => {
  'use strict';

  const theme = window.theme || { strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
  const lock = () => theme.utils && theme.utils.scrollLock.lock();
  const unlock = () => theme.utils && theme.utils.scrollLock.unlock();

  /**
   * Index of the slide currently aligned in a horizontal scroll-snap track.
   * The last slide is detected from the scroll end, so it works with or without "peek".
   */
  const indexFromScroll = (track, slides) => {
    if (slides.length < 2) return 0;
    const { scrollLeft, scrollWidth, clientWidth } = track;
    if (scrollLeft + clientWidth >= scrollWidth - 2) return slides.length - 1;
    const step = slides[1].offsetLeft - slides[0].offsetLeft || clientWidth;
    return clamp(Math.round(scrollLeft / step), 0, slides.length - 1);
  };

  /* ==========================================================================
     Media gallery
     - Finger swipe = native scrolling (scroll-snap), so it feels like the OS.
     - No loop: index is always clamped between the first and the last slide.
     - Thumbnails are never centred. In "static" mode the strip never moves on its
       own; in "nearest" mode a hidden thumbnail is nudged to the closest edge.
       scrollIntoView() is never used (it would also scroll the page vertically).
     ========================================================================== */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbList = this.querySelector('[data-thumbs-list]');
      this.thumbs = this.thumbList ? [...this.thumbList.querySelectorAll('[data-thumb]')] : [];
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.counter = this.querySelector('[data-current]');
      this.hint = this.querySelector('[data-hint]');
      this.mode = this.dataset.thumbs === 'nearest' ? 'nearest' : 'static';
      this.index = -1;

      // Main track: follow the finger, update UI as it settles.
      // While an arrow/thumbnail jump animates, intermediate slides are ignored so the
      // highlighted thumbnail doesn't flicker back through every image on the way.
      this.track.addEventListener('scroll', () => {
        if (this.hint && this.track.scrollLeft > 4) this.hideHint();
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          const i = indexFromScroll(this.track, this.slides);
          if (this.pending != null) {
            if (i !== this.pending) return;
            this.pending = null;
          }
          this.setActive(i);
        });
      }, { passive: true });
      const cancelPending = () => { this.pending = null; };
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) => this.track.addEventListener(type, cancelPending, { passive: true }));
      this.track.addEventListener('scrollend', () => {
        if (this.pending == null) return;
        this.pending = null;
        this.setActive(indexFromScroll(this.track, this.slides));
      });

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1));

      this.track.addEventListener('keydown', (e) => {
        const keys = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(e.key in keys)) return;
        e.preventDefault();
        this.goTo(keys[e.key]);
      });

      // Thumbnails: tapping one must not move the strip (no auto-centring, no focus scroll).
      if (this.thumbList) {
        // The strip scrolls sideways on phones and up and down beside the photo on computers.
        const keepThumbScroll = () => {
          if (this.thumbScroll == null) return;
          this.thumbList.scrollLeft = this.thumbScroll[0];
          this.thumbList.scrollTop = this.thumbScroll[1];
        };
        this.thumbList.addEventListener('pointerdown', () => { this.thumbScroll = [this.thumbList.scrollLeft, this.thumbList.scrollTop]; }, { passive: true });
        this.thumbList.addEventListener('pointercancel', () => { this.thumbScroll = null; });
        this.thumbList.addEventListener('focusin', keepThumbScroll);
        this.thumbList.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-thumb]');
          if (!thumb) return;
          keepThumbScroll();
          this.thumbScroll = null;
          this.goTo(Number(thumb.dataset.thumb));
        });
      }

      // Computer mouse: drag the photos sideways like a finger swipe, then settle on the nearest one.
      this.setupMouseDrag();

      // Full-screen viewer
      this.lightbox = this.querySelector('[data-lightbox]');
      if (this.lightbox) this.setupLightbox();
      this.track.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (zoom && this.lightbox) this.openLightbox(zoom.closest('[data-media-id]').dataset.mediaId);
      });

      // Keep the current slide aligned when the viewport changes (rotation, resize).
      this.ro = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        if (this.index >= 0) this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: 'auto' });
      });
      this.ro.observe(this.track);

      // Variant with its own image: jump to it (still clamped, never wraps).
      this.onVariantMedia = (e) => {
        if (e.detail.section !== this.dataset.section) return;
        this.goToMedia(String(e.detail.mediaId));
      };
      document.addEventListener('variant:media', this.onVariantMedia);

      const start = clamp(Number(this.dataset.start) || 0, 0, this.slides.length - 1);
      if (start > 0) this.track.scrollTo({ left: this.slides[start].offsetLeft, behavior: 'auto' });
      this.setActive(start);
    }

    disconnectedCallback() {
      this.ro && this.ro.disconnect();
      document.removeEventListener('variant:media', this.onVariantMedia);
    }

    goTo(i, smooth = true) {
      if (!this.slides.length) return;
      const target = clamp(i, 0, this.slides.length - 1); // hard stops: no loop
      const mode = smooth ? behavior() : 'auto';
      if (mode === 'smooth' && target !== this.index) {
        this.pending = target;
        clearTimeout(this.pendingTimer);
        this.pendingTimer = setTimeout(() => { this.pending = null; }, 2000);
      }
      this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: mode });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
      if (i >= 0) this.goTo(i);
    }

    setActive(i) {
      if (i === this.index) return;
      const previous = this.slides[this.index];
      this.index = i;
      if (previous) previous.querySelectorAll('video').forEach((v) => v.pause());
      if (this.counter) this.counter.textContent = String(i + 1);
      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;
      this.thumbs.forEach((t, n) => {
        if (n === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.mode === 'nearest') this.revealThumb(i);
    }

    setupMouseDrag() {
      const track = this.track;
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let active = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || this.slides.length < 2) return;
        active = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!active) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return;
        if (!moved) {
          moved = true;
          track.classList.add('is-dragging');
        }
        track.scrollLeft = startScroll - dx;
      });
      const end = (e) => {
        if (!active) return;
        active = false;
        if (!moved) return;
        const dx = e.clientX - startX;
        const width = track.clientWidth || 1;
        const from = Math.round(startScroll / width);
        let target = Math.round(track.scrollLeft / width);
        if (target === from && Math.abs(dx) > width * 0.12) target = from + (dx < 0 ? 1 : -1);
        track.classList.remove('is-dragging');
        this.goTo(target);
        // Swallow the click that follows a drag so it doesn't open the full-screen viewer.
        const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 50);
      };
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      track.addEventListener('dragstart', (e) => e.preventDefault());
    }

    // Only used in "nearest" mode: slide a hidden thumbnail just inside the edge. Never centres.
    revealThumb(i) {
      const thumb = this.thumbs[i];
      if (!thumb || !this.thumbList) return;
      const item = thumb.parentElement;
      const list = this.thumbList;
      const pad = 16;
      // Computers show the strip as a column beside the photo, so it scrolls vertically there.
      const vertical = getComputedStyle(list).flexDirection === 'column';
      const start = vertical ? item.offsetTop : item.offsetLeft;
      const end = start + (vertical ? item.offsetHeight : item.offsetWidth);
      const scroll = vertical ? list.scrollTop : list.scrollLeft;
      const view = vertical ? list.clientHeight : list.clientWidth;
      const axis = vertical ? 'top' : 'left';
      if (start < scroll) {
        list.scrollTo({ [axis]: Math.max(0, start - pad), behavior: behavior() });
      } else if (end > scroll + view) {
        list.scrollTo({ [axis]: end - view + pad, behavior: behavior() });
      }
    }

    hideHint() {
      if (!this.hint) return;
      this.hint.style.transition = 'opacity 0.3s ease';
      this.hint.style.opacity = '0';
      const hint = this.hint;
      this.hint = null;
      setTimeout(() => hint.remove(), 320);
    }

    /* ---------- Lightbox ---------- */
    setupLightbox() {
      const lb = this.lightbox;
      this.lbTrack = lb.querySelector('[data-lb-track]');
      this.lbSlides = [...this.lbTrack.children];
      this.lbCurrent = lb.querySelector('[data-lb-current]');
      this.lbPrev = lb.querySelector('[data-lb-prev]');
      this.lbNext = lb.querySelector('[data-lb-next]');
      this.lbIndex = 0;

      this.lbTrack.addEventListener('scroll', () => {
        if (this.lbTicking) return;
        this.lbTicking = true;
        requestAnimationFrame(() => {
          this.lbTicking = false;
          const i = indexFromScroll(this.lbTrack, this.lbSlides);
          if (this.lbPending != null) {
            if (i !== this.lbPending) return;
            this.lbPending = null;
          }
          this.lbSetActive(i);
        });
      }, { passive: true });
      const cancelLbPending = () => { this.lbPending = null; };
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) => this.lbTrack.addEventListener(type, cancelLbPending, { passive: true }));
      this.lbTrack.addEventListener('scrollend', () => {
        if (this.lbPending == null) return;
        this.lbPending = null;
        this.lbSetActive(indexFromScroll(this.lbTrack, this.lbSlides));
      });

      lb.querySelector('[data-lb-close]').addEventListener('click', () => lb.close());
      lb.addEventListener('close', () => this.onLightboxClose());
      this.lbPrev && this.lbPrev.addEventListener('click', () => this.lbGoTo(this.lbIndex - 1));
      this.lbNext && this.lbNext.addEventListener('click', () => this.lbGoTo(this.lbIndex + 1));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.lbGoTo(this.lbIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.lbGoTo(this.lbIndex + 1); }
      });

      // Tap to zoom where you tapped; pan with a finger; tap again to zoom out.
      lb.querySelectorAll('[data-lb-zoom]').forEach((zone) => {
        zone.addEventListener('click', (e) => this.toggleZoom(zone, e));
      });
    }

    openLightbox(mediaId) {
      const lb = this.lightbox;
      const i = Math.max(0, this.lbSlides.findIndex((s) => s.dataset.mediaId === mediaId));
      if (typeof lb.showModal === 'function') lb.showModal();
      else lb.setAttribute('open', '');
      lock();
      requestAnimationFrame(() => {
        this.lbTrack.scrollTo({ left: this.lbSlides[i].offsetLeft, behavior: 'auto' });
        this.lbIndex = -1;
        this.lbSetActive(i);
      });
    }

    onLightboxClose() {
      unlock();
      this.lbSlides.forEach((s) => this.resetZoom(s.querySelector('[data-lb-zoom]')));
      // Bring the page gallery to the image the visitor was looking at.
      const slide = this.lbSlides[this.lbIndex];
      if (slide) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === slide.dataset.mediaId);
        if (i >= 0) this.goTo(i, false);
      }
    }

    lbGoTo(i) {
      const target = clamp(i, 0, this.lbSlides.length - 1); // no loop in the viewer either
      this.lbSlides.forEach((s) => this.resetZoom(s.querySelector('[data-lb-zoom]')));
      const mode = behavior();
      if (mode === 'smooth' && target !== this.lbIndex) {
        this.lbPending = target;
        clearTimeout(this.lbPendingTimer);
        this.lbPendingTimer = setTimeout(() => { this.lbPending = null; }, 2000);
      }
      this.lbTrack.scrollTo({ left: this.lbSlides[target].offsetLeft, behavior: mode });
      this.lbSetActive(target);
    }

    lbSetActive(i) {
      if (i === this.lbIndex) return;
      const prev = this.lbSlides[this.lbIndex];
      if (prev) this.resetZoom(prev.querySelector('[data-lb-zoom]'));
      this.lbIndex = i;
      if (this.lbCurrent) this.lbCurrent.textContent = String(i + 1);
      if (this.lbPrev) this.lbPrev.disabled = i <= 0;
      if (this.lbNext) this.lbNext.disabled = i >= this.lbSlides.length - 1;
    }

    toggleZoom(zone, e) {
      if (zone.classList.contains('is-zoomed')) {
        this.resetZoom(zone);
        return;
      }
      const img = zone.querySelector('img');
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const rx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      const ry = clamp((e.clientY - rect.top) / rect.height, 0, 1);
      zone.classList.add('is-zoomed');
      this.lbTrack.style.overflowX = 'hidden'; // panning moves the image, not the slides
      requestAnimationFrame(() => {
        zone.scrollLeft = rx * zone.scrollWidth - zone.clientWidth / 2;
        zone.scrollTop = ry * zone.scrollHeight - zone.clientHeight / 2;
      });
    }

    resetZoom(zone) {
      if (!zone || !zone.classList.contains('is-zoomed')) return;
      zone.classList.remove('is-zoomed');
      zone.scrollTo(0, 0);
      this.lbTrack.style.overflowX = '';
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ==========================================================================
     Variant picker
     ========================================================================== */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.section = this.dataset.section;
      this.fieldsets = [...this.querySelectorAll('fieldset')];
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    selectedOptions() {
      return this.fieldsets.map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      this.fieldsets.forEach((fs, i) => {
        const label = fs.querySelector('[data-option-value]');
        if (label) label.textContent = options[i] || '';
      });
      this.updateAvailability();

      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      const form = document.getElementById(`ProductForm-${this.section}`);
      const idInput = form && form.querySelector('[data-variant-id]');

      if (!variant) {
        if (idInput) idInput.value = '';
        this.setButtons(false, theme.strings.unavailable);
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.setButtons(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      window.history.replaceState(window.history.state, '', `${this.dataset.url}?variant=${variant.id}`);

      if (variant.featured_media) {
        document.dispatchEvent(new CustomEvent('variant:media', {
          detail: { section: this.section, mediaId: variant.featured_media.id }
        }));
      }
      this.renderDynamic(variant);
    }

    // Strike through values that have no available variant with the other current choices.
    updateAvailability() {
      const options = this.selectedOptions();
      this.fieldsets.forEach((fs, i) => {
        fs.querySelectorAll('input').forEach((input) => {
          const ok = this.variants.some((v) => v.available && v.options[i] === input.value &&
            v.options.every((value, j) => j === i || options[j] == null || value === options[j]));
          const pill = input.closest('.pill');
          if (pill) pill.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    setButtons(enabled, text) {
      [`ProductSubmit-${this.section}`, `StickySubmit-${this.section}`].forEach((id) => {
        const btn = document.getElementById(id);
        if (!btn) return;
        btn.disabled = !enabled;
        const label = btn.querySelector('[data-submit-text]') || btn;
        if (text) label.textContent = text;
      });
      const buyNow = document.getElementById(`BuyNow-${this.section}`);
      if (buyNow) buyNow.disabled = !enabled;
    }

    // Price, instalments, stock, SKU: re-rendered by Shopify for the chosen variant.
    async renderDynamic(variant) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${encodeURIComponent(this.section)}`, {
          signal: this.controller.signal
        });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        document.querySelectorAll(`[data-dynamic="${this.section}"]`).forEach((el) => {
          const fresh = doc.getElementById(el.id);
          if (fresh) el.innerHTML = fresh.innerHTML;
          document.dispatchEvent(new CustomEvent('variant:change'));
        });
      } catch (err) {
        /* aborted by a newer selection, or offline: keep current content */
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Delivery dates (kept fresh even when the page is cached) ---------- */
  const day = (offset) => { const d = new Date(); d.setDate(d.getDate() + offset); return d; };
  const fmtLong = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const fmtDay = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
  const refreshEta = () => {
    document.querySelectorAll('[data-eta]').forEach((el) => {
      const d = day(Number(el.dataset.days || 0));
      el.textContent = (el.dataset.format === 'day' ? fmtDay : fmtLong).format(d);
    });
    document.querySelectorAll('[data-eta-range]').forEach((el) => {
      const a = day(Number(el.dataset.min || 0));
      const b = day(Number(el.dataset.max || 0));
      el.textContent = a.getMonth() === b.getMonth()
        ? `${a.getDate()}–${fmtDay.format(b)}`
        : `${fmtDay.format(a)} – ${fmtDay.format(b)}`;
    });
  };
  refreshEta();
  document.addEventListener('variant:change', refreshEta);
})();
